import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { _className, ComponentSync, EditableDBItemV3, LL_H_C, LL_V_L, openContent, scrollToTop, TS_Checkbox, TS_PropRenderer } from '@nu-art/thunderstorm-frontend/index';
import { FHIRResourceSubTypeParamType_Dynamic, FHIRResourceSubTypeParamType_Fixed } from '@app/fhir-shared';
import './Editor_FHIRResourceSubTypeParam.scss';
import { capitalizeFirstLetter } from '@nu-art/ts-common';
import { Component_SwitchView } from '@nu-art/permissions-frontend/ui/Component_SwitchView';
import { DefaultEditor_InputText, DefaultEditor_InputText_Optional, DefaultEditor_TextArea } from '@app/styles/frontend/ui-manager/ui-components';
import { ICONS } from '@app/styles';
import { ModuleFE_FHIRResourceParam, ModuleFE_FHIRResourceParamValue } from '../../../_entity.js';
const paramToolTipTexts = {
    [FHIRResourceSubTypeParamType_Fixed]: 'This param has a fixed value when defining it in a FHIR Query',
    [FHIRResourceSubTypeParamType_Dynamic]: 'This param gets a value from runtime data',
    singleValue: 'FHIR expects this param to pass a single value',
    multiValue: 'FHIR expects this param to pass an array of values',
    optional: 'this parameter is optional in the query',
    mandatory: 'this parameter is mandatory in the query',
    postFiltered: 'Post filtered params add a layer of filtration in FHIR on the query results',
};
const viewMode_View = 'view';
const viewMode_Edit = 'edit';
export class Editor_FHIRResourceSubTypeParam extends ComponentSync {
    paramRef = React.createRef();
    // ######################## Lifecycle ########################
    deriveStateFromProps(nextProps, state) {
        state.editable = this.getEditable(nextProps.param);
        state.editMode = nextProps.editMode;
        state.subTypeScrollRef = nextProps.subTypeScrollRef;
        return state;
    }
    componentDidMount() {
        //Only once when this component is first rendered, check if scroll needs to be triggered
        if (this.props.triggerScroll)
            scrollToTop(this.paramRef, this.state.subTypeScrollRef);
    }
    // ######################## Logic ########################
    getEditable(param) {
        return new EditableDBItemV3(param, ModuleFE_FHIRResourceParam)
            .setAutoSave(!!param._id)
            .setDebounceTimeout(300)
            .setOnSaveCompleted(() => this.forceUpdate());
    }
    getViewMode = () => this.state.editMode ? viewMode_Edit : viewMode_View;
    paramTooltip = (key) => {
        return openContent.tooltip.top('editor__fhir-resource-sub-type-param__tooltip', () => _jsx("p", { children: paramToolTipTexts[key] }), { offset: 10 });
    };
    createNewParamValue = async () => {
        const param = this.state.editable.item;
        try {
            const newValue = {
                paramId: param._id,
                value: 'new-value',
                label: 'New Value',
            };
            await ModuleFE_FHIRResourceParamValue.v1.upsert(newValue).executeSync();
            this.forceUpdate();
        }
        catch (err) {
            this.logError(err);
        }
    };
    // ######################## Render ########################
    render() {
        return _jsxs(LL_V_L, { className: 'editor__fhir-resource-sub-type-param', innerRef: this.paramRef, children: [this.renderTitle(), this.renderChecks(), this.renderEditor_Description(), this.renderEditor_Values(), this.renderDelete()] });
    }
    renderTitle = () => {
        const param = this.state.editable;
        return _jsxs(LL_H_C, { className: 'editor__fhir-resource-sub-type-param__param-title', children: [_jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                        { key: viewMode_View, renderer: () => param.item.key },
                        { key: viewMode_Edit, renderer: () => _jsx(DefaultEditor_InputText, { editable: param, prop: 'key' }) }
                    ] }), _jsxs("div", { className: 'editor__fhir-resource-sub-type-param__param-type', ...this.paramTooltip(param.item.type), children: ["[", capitalizeFirstLetter(param.item.type), "]"] }), _jsx("div", { className: 'editor__fhir-resource-sub-type-param__param-multi-value', ...this.paramTooltip(param.item.isMultiValue ? 'multiValue' : 'singleValue'), children: param.item.isMultiValue ? '[MultiValue]' : '[SingleValue]' }), !this.state.editMode &&
                    _jsx("div", { className: 'editor__fhir-resource-sub-type-param__param-is-optional', ...this.paramTooltip(param.item.isOptional ? 'optional' : 'mandatory'), children: param.item.isOptional ? ' [Optional]' : ' [Mandatory]' }), !this.state.editMode && param.item.isPostFiltered &&
                    _jsx("div", { className: 'editor__fhir-resource-sub-type-param__param-is-post-filtered', ...this.paramTooltip('postFiltered'), children: "[Post Filtered]" })] });
    };
    renderChecks = () => {
        if (!this.state.editMode)
            return;
        const param = this.state.editable;
        return _jsxs(LL_H_C, { className: 'editor__fhir-resource-sub-type-param__checks', children: [_jsx(TS_Checkbox, { icon: _jsx(ICONS.v.component, {}), checked: param.item.isOptional, onCheck: () => param.updateObj({ isOptional: !param.item.isOptional }), children: "Is Optional" }), _jsx(TS_Checkbox, { icon: _jsx(ICONS.v.component, {}), checked: param.item.isPostFiltered, onCheck: () => param.updateObj({ isPostFiltered: !param.item.isPostFiltered }), children: "Is Post Filtered" })] });
    };
    renderEditor_Description = () => {
        const param = this.state.editable;
        return _jsx(TS_PropRenderer.Vertical, { label: 'Description', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                    { key: viewMode_View, renderer: () => _jsx("div", { className: 'editor__fhir-resource-sub-type__value', children: param.item.description ?? 'N/A' }) },
                    { key: viewMode_Edit, renderer: () => _jsx(DefaultEditor_TextArea, { resizeWithText: true, editable: param, prop: 'description' }) }
                ] }) });
    };
    renderDelete = () => {
        if (!this.state.editMode)
            return;
        const param = this.state.editable;
        return _jsx(ICONS.bin.component, { className: 'editor__fhir-resource-sub-type-param__delete', onClick: () => param.delete() });
    };
    // ######################## Render - Fixed Specific ########################
    renderEditor_Values = () => {
        const param = this.state.editable;
        if (param.item.type !== FHIRResourceSubTypeParamType_Fixed)
            return;
        const values = ModuleFE_FHIRResourceParamValue.cache.filter(value => value.paramId === param.item._id);
        return _jsxs(TS_PropRenderer.Vertical, { label: this.renderEditor_Values_Label, children: [!values.length && _jsx("div", { className: 'editor__fhir-resource-sub-type__value', children: "No values defined for this param" }), !!values.length && _jsx(LL_H_C, { className: 'editor__fhir-resource-sub-type-param__values', children: values.map(this.renderEditor_Values_Value) })] });
    };
    renderEditor_Values_Label = () => {
        return _jsxs(_Fragment, { children: ["Values", this.state.editMode && _jsx(ICONS.advisor_avn_add.component, { onClick: this.createNewParamValue })] });
    };
    renderEditor_Values_Value = (value) => {
        const editable = new EditableDBItemV3(value, ModuleFE_FHIRResourceParamValue);
        editable.setAutoSave(true);
        editable.setDebounceTimeout(300);
        editable.setOnSaveCompleted(() => this.forceUpdate());
        const className = _className('editor__fhir-resource-sub-type-param__values__value', this.state.editMode && 'edit');
        return _jsxs(LL_V_L, { className: className, children: [_jsx(TS_PropRenderer.Horizontal, { label: 'Value', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                            { key: viewMode_View, renderer: () => _jsx("div", { className: 'editor__fhir-resource-sub-type__value', children: editable.item.value ?? 'N/A' }) },
                            { key: viewMode_Edit, renderer: () => _jsx(DefaultEditor_InputText, { editable: editable, prop: 'value' }) }
                        ] }) }), _jsx(TS_PropRenderer.Horizontal, { label: 'Label', children: _jsx(Component_SwitchView, { mode: this.getViewMode(), modes: [
                            { key: viewMode_View, renderer: () => _jsx("div", { className: 'editor__fhir-resource-sub-type__value', children: editable.item.label ?? 'N/A' }) },
                            { key: viewMode_Edit, renderer: () => _jsx(DefaultEditor_InputText_Optional, { editable: editable, prop: 'label' }) }
                        ] }) }), this.state.editMode && _jsx(ICONS.bin.component, { onClick: async () => {
                        try {
                            await editable.delete();
                            this.forceUpdate();
                        }
                        catch (err) {
                            this.logError(err);
                        }
                    } })] }, value._id);
    };
}
//# sourceMappingURL=Editor_FHIRResourceSubTypeParam.js.map